import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { GlassPanel } from "@/components/GlassPanel";
import { StatusBadge } from "@/components/StatusBadge";
import { CtaLink } from "@/components/Button";
import { Keycap } from "@/components/keycap/Keycap";
import { MarketplaceCatalogue } from "@/components/marketplace/MarketplaceCatalogue";
import { marketplaceListings } from "@/content/marketplace";
import { signInCta, signUpCta } from "@/config/site";
import { routeSeo } from "@/content/seo";

const roles = [
  {
    title: "Creators",
    body: "Author, qualify and publish components, as free contributions or commercial listings once publishing opens.",
  },
  {
    title: "Buyers",
    body: "Find qualified components and reuse them under a stated licence, instead of rebuilding the same part.",
  },
  {
    title: "Teams",
    body: "Maintain a shared library of components a team has already qualified, visible only to that team.",
  },
  {
    title: "Organisations",
    body: "Govern which components an organisation's teams may install, and manage licensing at the organisation level.",
  },
];

export function MarketplacePage() {
  const seo = routeSeo["/marketplace"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/marketplace" />

      <section className="container page-hero">
        <p className="eyebrow">Marketplace</p>
        <h1 className="page-hero-title">A public catalogue of what Devory is building</h1>
        <p className="hero-stage">
          <StatusBadge status="live" />
        </p>
        <p className="page-hero-lede">
          The Devory Marketplace is not a storefront. It is where already-qualified components
          become discoverable, by engineers browsing directly and by AI agents searching through the
          same catalogue. Nothing here is trusted because it is popular. It is trusted because it
          passed the qualification pipeline described below before it was ever listed. Browsing is
          public today, and the real publishing/ordering pipeline — publisher accounts, listings,
          entitlement-gated orders and commission accounting — is live. No listings have been
          published to it yet, so there is nothing to actually buy or install in practice today.
        </p>
      </section>

      <section className="container section-tight page-diagram">
        <Keycap id="publish" size="xl" state="active" />
      </section>

      <section className="container section" aria-labelledby="catalogue-heading">
        <SectionHeading
          level={2}
          title={<span id="catalogue-heading">Browse the catalogue</span>}
          lede="Search, or filter by category or availability. Every result below is a real catalogue entry, not a simulated search."
        />
        <MarketplaceCatalogue listings={marketplaceListings} />
        <p className="marketplace-preview-note">
          Nothing shown in this preview catalogue can currently be bought or installed — these are
          honest descriptions of real component inventory, not live listings. The real
          publisher/listing/ordering pipeline is separately live (see below); it simply has no
          published listings in it yet. Engineering status (is it built) and availability (can you
          get it) are shown separately and honestly.
        </p>
      </section>

      <section className="container section" aria-labelledby="roles-heading">
        <SectionHeading
          level={2}
          title={<span id="roles-heading">Four roles the Marketplace is designed around</span>}
        />
        <div className="grid-2">
          {roles.map((role) => (
            <GlassPanel as="article" key={role.title} className="audience-card">
              <h3>{role.title}</h3>
              <p>{role.body}</p>
            </GlassPanel>
          ))}
        </div>
      </section>

      <section
        className="container-narrow section-tight prose"
        aria-labelledby="ai-discovery-heading"
      >
        <p className="eyebrow">How AI uses this catalogue</p>
        <h2 id="ai-discovery-heading">Discovery instead of regeneration</h2>
        <p>
          An AI coding assistant asked to add authentication, storage or payments will usually
          generate that code from scratch, then spend more of its context debugging and refining
          what it just wrote. The Marketplace gives it another option. Through Devory Autonomous
          User Interface, an agent can search this same catalogue, read a component&rsquo;s
          interface, and compose it into the application instead of reimplementing it. The result is
          less repeated implementation work, more consistent output across projects, and a reasoning
          budget spent on the problem that is actually new rather than the one that already has a
          qualified answer.
        </p>
      </section>

      <section className="container section-tight" aria-labelledby="marketplace-status-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="marketplace-status-heading">What exists today</span>}
          />
          <ul className="list-check">
            <li>Browsing this catalogue is public; no account is required to look</li>
            <li>
              Publishing a listing, buying it, and the commission/entitlement accounting behind
              that are all real and live — the same billing rails Devory Cloud subscriptions use
            </li>
            <li>
              No listings have been published yet, so there is nothing to actually buy or install
              in practice today
            </li>
            <li>
              Component qualification (see the qualification page) is the real prerequisite this
              depends on
            </li>
            <li>
              There is no star rating, download count or popularity score anywhere on this page, and
              there will not be one. A recommendation here always traces to a real, checkable
              qualification result, never an invented statistic
            </li>
            <li>
              External AI agents may discover Marketplace-adjacent capabilities through Devory AUI
              once its integration is externally reachable. See the Agents and integrations category
              above
            </li>
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>Have a Devory account?</h2>
          <p>
            Sign in to Devory Cloud to see qualification records and organisation-scoped access.
          </p>
          <div className="cta-banner-actions">
            <CtaLink to={signInCta.path} variant="secondary">
              {signInCta.label}
            </CtaLink>
            <CtaLink to={signUpCta.path} variant="primary">
              {signUpCta.label}
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
